import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react'
import styles from './styles.module.css'
import { Loader, useToast } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { PersonaInlineInquiry } from '@afterpaytouch/identity'
import { setLoggedInLoadingMessageKey, setSkipBiometricCheck, useAppDispatch, useAppRouter } from '../../../state'
import { SupportedLocale } from '@afterpay/types'
import { Route } from '../../../config/router'
import { PERSONA_ENV_ID, PERSONA_TEMPLATE_ID } from '../../../model/identity'
import { useBiometricInquiryId } from '../hooks'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'

export const PersonaHardId: FunctionComponent = () => {
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()
  const router = useAppRouter()
  const language = router.locale === ('fr-CA' as SupportedLocale) ? 'fr' : 'en-US'
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { inquiryId, isCreateInquiryIdError, isCreateInquiryIdSuccess, sessionToken } = useBiometricInquiryId()
  const [shouldRenderPersona, setShouldRenderPersona] = useState(false)
  const [templateId, setTemplateId] = useState<string | undefined>(PERSONA_TEMPLATE_ID)
  const dispatch = useAppDispatch()

  const handleOnComplete = (): void => {
    logEvent(TrackingEvent.VIEWED_PERSONA_VERIFICATION_SUCCESS)
    dispatch(setSkipBiometricCheck(true))
    dispatch(setLoggedInLoadingMessageKey('verifyIdentity'))
    router.push(Route.VERIFYING_BIOMETRIC)
  }

  useEffect(() => {
    if (isCreateInquiryIdSuccess) {
      if (typeof inquiryId !== 'undefined') {
        // In QA/prod environments, we use inquiryId and set templateId to undefined
        setTemplateId(undefined)
      }
      setShouldRenderPersona(true)
    }
  }, [inquiryId, isCreateInquiryIdSuccess])

  const setToast = useToast()

  const handleOnError = useCallback((): void => {
    setToast({
      message: t('verifyIdentity:error:toast'),
      kind: 'Failure',
    })
    router.push(Route.VERIFY_IDENTITY_START)
  }, [router, setToast, t])

  useEffect(() => {
    if (isCreateInquiryIdError) {
      logEvent(TrackingEvent.VIEWED_PERSONA_VERIFICATION_ERROR_TOAST, { error: 'Failed to create inquiry ID' })
      handleOnError()
    }
  }, [isCreateInquiryIdError, handleOnError, logEvent])
  const handleOnLoad = (): void => {
    setLoading(false)
  }

  const renderLoading = (): ReactNode => {
    return (
      <div className={styles.loadingText}>
        <Loader testNameSpace='persona-hard-id' textProps={{ size: 'L', bold: true }} text={t('verifyIdentity:start:loading')} />
      </div>
    )
  }

  const handlePersonaError = (error: unknown): void => {
    logEvent(TrackingEvent.VIEWED_PERSONA_VERIFICATION_ERROR_TOAST, { error })
    handleOnError()
  }

  const handlePersonaEvent = (eventName: string): void => {
    logEvent(TrackingEvent.VIEWED_PERSONA_VERIFICATION_EVENT, { eventName })
    if (eventName === 'cancel') {
      // Navigate back to the start of verification flow
      handlePersonaError(eventName)
    }
  }

  if (!shouldRenderPersona) {
    return renderLoading()
  }

  return (
    <>
      {loading && renderLoading()}
      <div className={styles.personaContainer}>
        <PersonaInlineInquiry
          onError={handlePersonaError}
          language={language}
          onLoad={handleOnLoad}
          inquiryId={inquiryId}
          sessionToken={sessionToken}
          templateId={templateId}
          // Only set environmentId for sandbox environment
          environmentId={process.env.NODE_ENV === 'development' ? PERSONA_ENV_ID : undefined}
          onComplete={handleOnComplete}
          onEvent={handlePersonaEvent}
        />
      </div>
    </>
  )
}
